import { BlankSlate } from "@msuite/ui/blank-slate";
import { Button, ButtonGroup } from "@msuite/ui/button";
import { ContentCard } from "@msuite/ui/content-card";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Fragment } from "react";
import { useCitadel } from "@/hooks/use-citadel";
import { useUserEmployments } from "@/hooks/use-user-employments";
import { AddEmploymentModal } from "@/modals/users/add-employment-modal";
import { EditEmploymentModal } from "@/modals/users/edit-employment-modal";
import type { UserPanelBaseProps } from "../types";
import { UserEmploymentItem } from "./user-employment-item";

export function UserEmployment({ userId }: UserPanelBaseProps) {
	/** Hooks */
	const { t } = useTranslation();
	const addEmploymentModal = useDisclosure();
	const editEmploymentModal = useDisclosure();
	const { citadel } = useCitadel();

	/** Computed */
	// The server binds `read Employment` to the employee the records are about:
	// a member holds it for their own sheet and is refused a colleague's, so the
	// check carries the row the route would check, and the panel neither asks
	// nor renders when the answer is no.
	const canReadEmployment =
		citadel?.can("read", "Employment", { orgId: citadel.orgId, userId }) === true;
	// Filing a new contract is an HR right; without it the blank slate stays a
	// statement that there is no employment rather than an invitation to add one.
	const canCreateEmployment = citadel?.can("create", "Employment") === true;
	// Amending the running contract is its own right — the patch route answers
	// 403 for a caller who may file contracts but not rewrite them.
	const canUpdateEmployment = citadel?.can("update", "Employment") === true;

	const { data, isLoading, refetch } = useUserEmployments(userId, { enabled: canReadEmployment });

	/** Guard */
	if (!canReadEmployment) return null;

	/** Render */
	return (
		<Fragment>
			<ContentCard
				title={t("personnel.employment.employment")}
				icon={icon("User2")}
			>
				{!data?.currentEmployment ? (
					<BlankSlate
						isLoading={isLoading}
						onClick={canCreateEmployment ? addEmploymentModal.onOpen : undefined}
						label={t("personnel.employment.no_employment_available")}
					/>
				) : (
					<Fragment>
						<UserEmploymentItem employment={data.currentEmployment} />
						<ButtonGroup
							className="justify-end"
							isAttached={false}
						>
							{canUpdateEmployment && (
								<Button
									icon={<Icon name="Edit" />}
									onClick={editEmploymentModal.onOpen}
								>
									{t("general.edit")}
								</Button>
							)}
							{canCreateEmployment && (
								<Button
									variant="primary"
									icon={<Icon name="Plus" />}
									onClick={addEmploymentModal.onOpen}
								>
									{t("general.add")}
								</Button>
							)}
						</ButtonGroup>
					</Fragment>
				)}
			</ContentCard>
			{addEmploymentModal.isOpen && (
				<AddEmploymentModal
					isOpen={addEmploymentModal.isOpen}
					onClose={addEmploymentModal.onClose}
					userId={userId}
				/>
			)}
			{editEmploymentModal.isOpen && data?.currentEmployment && (
				<EditEmploymentModal
					isOpen={editEmploymentModal.isOpen}
					onClose={editEmploymentModal.onClose}
					userId={userId}
					employmentId={data.currentEmployment.id}
					onComplete={refetch}
				/>
			)}
		</Fragment>
	);
}
